import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPPORT_NAV, SLA_META, TICKET_STATUS_COLORS, PRIORITY_COLORS } from './nav';

export interface Ticket {
    id: number; subject: string; category: string; priority: string; status: string;
    clinic: string | null; submitted_by: string | null; assigned_to: string | null; assigned_to_id: number | null;
    age_hours: number; sla_risk: string; created: string; updated: string;
}

const LiveQueue: React.FC = () => {
    const { api } = useInternalApi();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Live Queue - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('admin-dashboard/support/live-queue/').then(res => {
            if (res.data?.status === 'success') setTickets(res.data.data || []);
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const breached = tickets.filter(t => t.sla_risk === 'breached').length;
    const atRisk = tickets.filter(t => t.sla_risk === 'at_risk').length;
    const unassigned = tickets.filter(t => !t.assigned_to_id).length;

    return (
        <InternalShell activeDashboard="support_center" subNav={SUPPORT_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faInbox} className="me-2 text-primary" />Live Queue</h2>
            <p className="text-muted mb-4">Open tickets ranked by how long they've been waiting</p>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Open Tickets</div><h4>{tickets.length}</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className={`card border-0 shadow-sm ${breached > 0 ? 'border-danger' : ''}`}><div className="card-body">
                                <div className="text-muted small mb-1">SLA Breached (&gt;72h)</div>
                                <h4 className={breached > 0 ? 'text-danger' : ''}>{breached}</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">At Risk (&gt;24h)</div>
                                <h4 className={atRisk > 0 ? 'text-warning' : ''}>{atRisk}</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Unassigned</div><h4>{unassigned}</h4>
                            </div></div>
                        </div>
                    </div>

                    <div className="alert alert-secondary small">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        Tickets carry no stored SLA target, so risk is derived from age: over 24 hours open is <strong>at risk</strong>, over 72 hours is <strong>breached</strong>.
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {tickets.length === 0 ? (
                                <div className="text-center py-5 text-muted">Queue is clear — no open tickets.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Subject</th><th>Clinic</th><th>Priority</th><th>Status</th><th>Age</th><th>SLA</th><th>Assigned</th></tr></thead>
                                    <tbody>
                                        {tickets.map(t => (
                                            <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/support/workbench?ticket=${t.id}`)}>
                                                <td>{t.subject}<div className="small text-muted">#{t.id} · {t.category}</div></td>
                                                <td>{t.clinic || '—'}</td>
                                                <td><span className={`badge bg-${PRIORITY_COLORS[t.priority] || 'secondary'}`}>{t.priority}</span></td>
                                                <td><span className={`badge bg-${TICKET_STATUS_COLORS[t.status] || 'secondary'}`}>{t.status}</span></td>
                                                <td>{t.age_hours < 24 ? `${t.age_hours.toFixed(1)}h` : `${Math.floor(t.age_hours / 24)}d`}</td>
                                                <td><span className={`badge bg-${SLA_META[t.sla_risk]?.color}`}>{SLA_META[t.sla_risk]?.label}</span></td>
                                                <td>{t.assigned_to || <span className="text-muted">Unassigned</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default LiveQueue;
